import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { UpsertAdminUnitRequest } from "../../models/RequestModels/UpsertAdminUnitRequest";
import { AdminAccountResponse } from "../../models/ResponseModels/AdminAccountResponse";
import { AdminUnitResponse } from "../../models/ResponseModels/AdminUnitResponse";
import { useAppSelector } from "../../store/hooks";
import AppButton from "../button/AppButton";
import AppSelectInput from "../inputs/AppSelectInput";
import AppTextInput from "../inputs/AppTextInput";

interface UpsertUnitModalProps {
  open: boolean;
  account: AdminAccountResponse | null;
  unit: AdminUnitResponse | null;
  initialPropertyId: number | null;
  onClose: () => void;
  onSubmit: (body: UpsertAdminUnitRequest) => void;
}

const calendarUrl = (
  unit: AdminUnitResponse,
  provider: "airbnb" | "booking",
): string => {
  return (
    unit.external_calendars?.find((calendar) => calendar.provider === provider)
      ?.ical_url || ""
  );
};

const UpsertUnitModal = ({
  open,
  account,
  unit,
  initialPropertyId,
  onClose,
  onSubmit,
}: UpsertUnitModalProps) => {
  const { saving, fieldErrors } = useAppSelector(
    (state) => state.adminAccounts,
  );

  const [form, setForm] = useState<UpsertAdminUnitRequest>({
    property_id: 0,
    name: "",
    type: "apartment",
    capacity: 1,
    default_price_per_night: undefined,
    active: true,
    default_cleaner_id: null,
    airbnb_ical_url: "",
    booking_ical_url: "",
  });

  const properties = useMemo(
    () => account?.properties || [],
    [account?.properties],
  );

  const cleaners = useMemo(
    () =>
      (account?.users || []).filter(
        (user) => user.role === "cleaner" && user.is_active !== false,
      ),
    [account?.users],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (unit) {
      setForm({
        property_id: unit.property_id,
        name: unit.name,
        type: unit.type as UpsertAdminUnitRequest["type"],
        capacity: unit.capacity,
        default_price_per_night:
          unit.default_price_per_night === null ||
          unit.default_price_per_night === undefined
            ? undefined
            : Number(unit.default_price_per_night),
        active: unit.active,
        default_cleaner_id: unit.default_cleaner_id || null,
        airbnb_ical_url: calendarUrl(unit, "airbnb"),
        booking_ical_url: calendarUrl(unit, "booking"),
      });

      return;
    }

    setForm({
      property_id: initialPropertyId || properties[0]?.id || 0,
      name: "",
      type: "apartment",
      capacity: 1,
      default_price_per_night: undefined,
      active: true,
      default_cleaner_id: null,
      airbnb_ical_url: "",
      booking_ical_url: "",
    });
  }, [initialPropertyId, open, properties, unit]);

  const propertyOptions = useMemo(
    () =>
      properties.map((property) => ({
        label: property.name,
        value: String(property.id),
      })),
    [properties],
  );

  const cleanerOptions = useMemo(
    () => [
      {
        label: "No default housekeeper",
        value: "",
      },
      ...cleaners.map((cleaner) => ({
        label: `${cleaner.name} (${cleaner.email})`,
        value: String(cleaner.id),
      })),
    ],
    [cleaners],
  );

  const updateField = <K extends keyof UpsertAdminUnitRequest>(
    key: K,
    value: UpsertAdminUnitRequest[K],
  ) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const getError = (key: string) => fieldErrors[key]?.[0];

  const handleSubmit = () => {
    onSubmit({
      ...form,

      property_id: Number(form.property_id),

      capacity: Number(form.capacity || 1),

      default_price_per_night:
        form.default_price_per_night === undefined ||
        form.default_price_per_night === null ||
        String(form.default_price_per_night).trim() === ""
          ? null
          : Number(form.default_price_per_night),

      default_cleaner_id: form.default_cleaner_id
        ? Number(form.default_cleaner_id)
        : null,

      airbnb_ical_url: form.airbnb_ical_url?.trim() || null,

      booking_ical_url: form.booking_ical_url?.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{unit ? "Edit unit" : "Add unit"}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, 1fr)",
              },
              gap: 2,
            }}
          >
            <AppSelectInput
              label="Property"
              value={String(form.property_id || "")}
              onChange={(value) => updateField("property_id", Number(value))}
              options={propertyOptions}
              error={getError("property_id")}
              disabled={properties.length === 0}
            />

            <AppTextInput
              label="Unit name"
              value={form.name}
              onChange={(value) => updateField("name", value)}
              error={getError("name")}
              required
            />

            <AppSelectInput
              label="Type"
              value={form.type || "apartment"}
              onChange={(value) =>
                updateField(
                  "type",
                  value as NonNullable<UpsertAdminUnitRequest["type"]>,
                )
              }
              error={getError("type")}
              options={[
                {
                  label: "Apartment",
                  value: "apartment",
                },
                {
                  label: "Room",
                  value: "room",
                },
                {
                  label: "Studio",
                  value: "studio",
                },
                {
                  label: "House",
                  value: "house",
                },
                {
                  label: "Other",
                  value: "other",
                },
              ]}
            />

            <AppTextInput
              label="Capacity"
              type="number"
              value={form.capacity || 1}
              onChange={(value) => updateField("capacity", Number(value))}
              error={getError("capacity")}
            />

            <AppTextInput
              label="Default price per night"
              type="number"
              value={form.default_price_per_night ?? ""}
              onChange={(value) =>
                updateField(
                  "default_price_per_night",
                  value === "" ? null : Number(value),
                )
              }
              error={getError("default_price_per_night")}
            />

            <AppSelectInput
              label="Status"
              value={form.active === false ? "inactive" : "active"}
              onChange={(value) => updateField("active", value === "active")}
              options={[
                {
                  label: "Active",
                  value: "active",
                },
                {
                  label: "Inactive",
                  value: "inactive",
                },
              ]}
              error={getError("active")}
            />

            <Box
              sx={{
                gridColumn: "1 / -1",
              }}
            >
              <AppSelectInput
                label="Default housekeeper"
                value={String(form.default_cleaner_id || "")}
                onChange={(value) =>
                  updateField(
                    "default_cleaner_id",
                    value === "" ? null : Number(value),
                  )
                }
                options={cleanerOptions}
                error={getError("default_cleaner_id")}
              />
            </Box>

            <Box
              sx={{
                gridColumn: "1 / -1",
              }}
            >
              <AppTextInput
                label="Airbnb iCal import URL"
                value={form.airbnb_ical_url || ""}
                onChange={(value) => updateField("airbnb_ical_url", value)}
                error={getError("airbnb_ical_url")}
              />
            </Box>

            <Box
              sx={{
                gridColumn: "1 / -1",
              }}
            >
              <AppTextInput
                label="Booking.com iCal import URL"
                value={form.booking_ical_url || ""}
                onChange={(value) => updateField("booking_ical_url", value)}
                error={getError("booking_ical_url")}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </AppButton>

        <AppButton
          onClick={handleSubmit}
          loading={saving}
          disabled={properties.length === 0}
        >
          {unit ? "Save unit" : "Add unit"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpsertUnitModal;
